import React, { useState, useEffect } from 'react';
import { FaSearch, FaUserCircle, FaTimes, FaLock, FaSave, FaGraduationCap, FaPhone, FaEnvelope, FaMapMarkerAlt, FaIdCard, FaPlus, FaUserPlus } from 'react-icons/fa';
import API_BASE_URL from '../../config/api';

// ─── Add Student Modal ───────────────────────────────────────────────────────
const EMPTY_FORM = {
  full_name: '', email: '', password: '', phone: '', address: '',
  birthday: '', grade: '', medium: 'Sinhala', district: '',
  institute_name: '', school_name: '', guardian_name: '', guardian_phone: ''
};

const AddStudentModal = ({ onClose, onAdded }) => {
  const [form, setForm]         = useState({ ...EMPTY_FORM });
  const [saving, setSaving]     = useState(false);
  const [result, setResult]     = useState(null); // { type, text, index_number }

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setResult(null);
    try {
      const res  = await fetch(`${API_BASE_URL}/admin/add_student.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form)
      });
      const data = await res.json();
      if (data.status === 'success') {
        setResult({ type: 'success', text: data.message, index: data.index_number });
        setForm({ ...EMPTY_FORM });
        onAdded();
      } else {
        setResult({ type: 'error', text: data.message });
      }
    } catch { setResult({ type: 'error', text: 'Failed to connect to server.' }); }
    finally { setSaving(false); }
  };

  const GRADES    = ['Grade 6','Grade 7','Grade 8','Grade 9','Grade 10','Grade 11','Grade 12','Grade 13','Revision','Paper Class'];
  const DISTRICTS = ['Ampara','Anuradhapura','Badulla','Batticaloa','Colombo','Galle','Gampaha','Hambantota','Jaffna','Kalutara','Kandy','Kegalle','Kilinochchi','Kurunegala','Mannar','Matale','Matara','Moneragala','Mullaitivu','Nuwara Eliya','Polonnaruwa','Puttalam','Ratnapura','Trincomalee','Vavuniya'];

  return (
    <>
      {/* Backdrop */}
      <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm z-40" onClick={onClose} />

      {/* Modal */}
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col overflow-hidden">

          {/* Header */}
          <div className="bg-gradient-to-r from-orange-500 to-red-500 px-6 py-5 flex items-center justify-between flex-shrink-0">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 bg-white/20 rounded-xl flex items-center justify-center">
                <FaUserPlus className="text-white" />
              </div>
              <div>
                <h2 className="font-black text-white text-base leading-tight">Add New Student</h2>
                <p className="text-orange-100 text-[9px] font-bold uppercase tracking-widest">Index number auto-generated</p>
              </div>
            </div>
            <button onClick={onClose} className="w-8 h-8 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors">
              <FaTimes className="text-white text-sm" />
            </button>
          </div>

          {/* Success Banner */}
          {result && (
            <div className={`px-6 py-3 text-xs font-bold border-b flex-shrink-0 flex items-center justify-between ${
              result.type === 'success'
                ? 'bg-green-50 border-green-100 text-green-700'
                : 'bg-red-50 border-red-100 text-red-700'
            }`}>
              <span>{result.text}</span>
              {result.index && (
                <span className="ml-3 px-2 py-0.5 bg-white rounded shadow text-orange-600 font-black tracking-widest">
                  {result.index}
                </span>
              )}
            </div>
          )}

          {/* Scrollable Form */}
          <form onSubmit={handleSubmit} className="overflow-y-auto p-6 space-y-5 flex-grow">

            {/* Required fields */}
            <div>
              <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest mb-3">Required Fields</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <MField label="Full Name *"       name="full_name"  value={form.full_name}  onChange={handleChange} required />
                <MField label="Email *"            name="email"     value={form.email}      onChange={handleChange} type="email" required />
                <MField label="Password *"         name="password"  value={form.password}   onChange={handleChange} type="password" required />
                <MField label="Phone"              name="phone"     value={form.phone}      onChange={handleChange} />
              </div>
            </div>

            <hr className="border-slate-100" />

            {/* Academic */}
            <div>
              <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest mb-3">Academic Info</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <MField label="Institute" name="institute_name" value={form.institute_name} onChange={handleChange} />
                <MField label="School"    name="school_name"    value={form.school_name}    onChange={handleChange} />
                <MSelectField label="Grade"  name="grade"   value={form.grade}   onChange={handleChange} options={GRADES} />
                <MSelectField label="Medium" name="medium"  value={form.medium}  onChange={handleChange} options={['Sinhala','English']} />
              </div>
            </div>

            <hr className="border-slate-100" />

            {/* Personal */}
            <div>
              <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest mb-3">Personal Info</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <MField label="Address"   name="address"   value={form.address}   onChange={handleChange} />
                <MField label="Birthday"  name="birthday"  value={form.birthday}  onChange={handleChange} type="date" />
                <MSelectField label="District" name="district" value={form.district} onChange={handleChange} options={DISTRICTS} />
                <MField label="Guardian Name"  name="guardian_name"  value={form.guardian_name}  onChange={handleChange} />
                <MField label="Guardian Phone" name="guardian_phone" value={form.guardian_phone} onChange={handleChange} />
              </div>
            </div>

            <button type="submit" disabled={saving}
              className="w-full py-3.5 bg-orange-500 hover:bg-orange-600 text-white font-black text-xs uppercase tracking-widest rounded-xl shadow transition-all active:scale-95 disabled:opacity-50 flex items-center justify-center gap-2">
              <FaPlus size={10} /> {saving ? 'Adding Student...' : 'Add Student'}
            </button>
          </form>
        </div>
      </div>
    </>
  );
};

const MField = ({ label, name, value, onChange, type = 'text', required = false }) => (
  <div>
    <label className="block text-[9px] font-black text-slate-400 uppercase tracking-widest mb-1">{label}</label>
    <input type={type} name={name} value={value} onChange={onChange} required={required}
      className="w-full bg-slate-50 border border-slate-100 rounded-lg px-3 py-2 text-xs font-bold text-slate-700 outline-none focus:border-orange-400 transition-all" />
  </div>
);

const MSelectField = ({ label, name, value, onChange, options }) => (
  <div>
    <label className="block text-[9px] font-black text-slate-400 uppercase tracking-widest mb-1">{label}</label>
    <select name={name} value={value} onChange={onChange}
      className="w-full bg-slate-50 border border-slate-100 rounded-lg px-3 py-2 text-xs font-bold text-slate-700 outline-none focus:border-orange-400 transition-all">
      <option value="">— Select —</option>
      {options.map(o => <option key={o} value={o}>{o}</option>)}
    </select>
  </div>
);

// ─── Student Detail Drawer ────────────────────────────────────────────────────
const StudentDrawer = ({ student, onClose, onSaved }) => {
  const [form, setForm] = useState({ ...student });
  const [newPassword, setNewPassword] = useState('');
  const [saving, setSaving] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [msg, setMsg] = useState(null);

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSaveProfile = async (e) => {
    e.preventDefault();
    setSaving(true);
    setMsg(null);
    try {
      const res = await fetch(`${API_BASE_URL}/admin/manage_student.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'update_profile', id: student.id, ...form })
      });
      const data = await res.json();
      setMsg({ type: data.status, text: data.message });
      if (data.status === 'success') onSaved();
    } catch { setMsg({ type: 'error', text: 'Failed to save.' }); }
    finally { setSaving(false); }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    if (newPassword.length < 6) { setMsg({ type: 'error', text: 'Password must be at least 6 characters.' }); return; }
    setResetting(true);
    setMsg(null);
    try {
      const res = await fetch(`${API_BASE_URL}/admin/manage_student.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'update_password', id: student.id, new_password: newPassword })
      });
      const data = await res.json();
      setMsg({ type: data.status, text: data.message });
      if (data.status === 'success') setNewPassword('');
    } catch { setMsg({ type: 'error', text: 'Failed to reset password.' }); }
    finally { setResetting(false); }
  };

  return (
    <>
      {/* Backdrop */}
      <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm z-40" onClick={onClose} />

      {/* Drawer */}
      <div className="fixed right-0 top-0 h-full w-full max-w-xl bg-white z-50 shadow-2xl flex flex-col overflow-hidden">

        {/* Header */}
        <div className="bg-gradient-to-r from-slate-800 to-slate-900 px-6 py-5 flex items-center justify-between flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-orange-500 flex items-center justify-center font-black text-white text-lg">
              {student.full_name?.charAt(0)}
            </div>
            <div>
              <p className="font-black text-white text-sm leading-tight">{student.full_name}</p>
              <p className="text-orange-400 text-[10px] font-bold uppercase tracking-widest">{student.index_number}</p>
            </div>
          </div>
          <button onClick={onClose} className="w-8 h-8 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors">
            <FaTimes className="text-white text-sm" />
          </button>
        </div>

        {/* Stats Bar */}
        <div className="grid grid-cols-2 border-b border-slate-100 flex-shrink-0">
          <div className="px-6 py-3 border-r border-slate-100">
            <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest">Enrolled Months</p>
            <p className="text-xl font-black text-slate-800">{student.enrolled_months ?? '—'}</p>
          </div>
          <div className="px-6 py-3">
            <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest">Total Paid</p>
            <p className="text-xl font-black text-orange-500">LKR {student.total_paid?.toLocaleString() ?? '0'}</p>
          </div>
        </div>

        {/* Alert message */}
        {msg && (
          <div className={`mx-6 mt-4 px-4 py-2.5 rounded-lg text-xs font-bold border flex-shrink-0 ${msg.type === 'success' ? 'bg-green-50 border-green-100 text-green-700' : 'bg-red-50 border-red-100 text-red-700'}`}>
            {msg.text}
          </div>
        )}

        {/* Scrollable Body */}
        <div className="flex-grow overflow-y-auto p-6 space-y-6">

          {/* Profile Form */}
          <form onSubmit={handleSaveProfile} className="space-y-5">
            <div className="flex items-center gap-2 mb-1">
              <FaGraduationCap className="text-orange-500" />
              <h3 className="font-black text-sm text-slate-800 uppercase tracking-widest">Profile Information</h3>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field label="Full Name" name="full_name" value={form.full_name} onChange={handleChange} icon={<FaIdCard />} />
              <Field label="Email" name="email" value={form.email} onChange={handleChange} type="email" icon={<FaEnvelope />} />
              <Field label="Phone" name="phone" value={form.phone} onChange={handleChange} icon={<FaPhone />} />
              <Field label="Birthday" name="birthday" value={form.birthday || ''} onChange={handleChange} type="date" icon={<FaIdCard />} />
              <Field label="Address" name="address" value={form.address || ''} onChange={handleChange} icon={<FaMapMarkerAlt />} />
              <Field label="School" name="school_name" value={form.school_name || ''} onChange={handleChange} icon={<FaGraduationCap />} />
              <Field label="Institute" name="institute_name" value={form.institute_name || ''} onChange={handleChange} icon={<FaGraduationCap />} />
              <SelectField label="Grade" name="grade" value={form.grade || ''} onChange={handleChange}
                options={['Grade 6','Grade 7','Grade 8','Grade 9','Grade 10','Grade 11','Grade 12','Grade 13','Revision','Paper Class']} />
              <SelectField label="Medium" name="medium" value={form.medium || ''} onChange={handleChange}
                options={['Sinhala','English']} />
              <SelectField label="District" name="district" value={form.district || ''} onChange={handleChange}
                options={['Ampara','Anuradhapura','Badulla','Batticaloa','Colombo','Galle','Gampaha','Hambantota','Jaffna','Kalutara','Kandy','Kegalle','Kilinochchi','Kurunegala','Mannar','Matale','Matara','Moneragala','Mullaitivu','Nuwara Eliya','Polonnaruwa','Puttalam','Ratnapura','Trincomalee','Vavuniya']} />
              <Field label="Guardian Name" name="guardian_name" value={form.guardian_name || ''} onChange={handleChange} icon={<FaIdCard />} />
              <Field label="Guardian Phone" name="guardian_phone" value={form.guardian_phone || ''} onChange={handleChange} icon={<FaPhone />} />
            </div>

            <button type="submit" disabled={saving}
              className="w-full py-3 bg-orange-500 hover:bg-orange-600 text-white font-black text-xs uppercase tracking-widest rounded-xl shadow transition-all active:scale-95 disabled:opacity-50 flex items-center justify-center gap-2">
              <FaSave size={11} /> {saving ? 'Saving...' : 'Save Profile Changes'}
            </button>
          </form>

          {/* Password Reset */}
          <form onSubmit={handleResetPassword} className="bg-slate-900 rounded-xl p-5 space-y-4">
            <div className="flex items-center gap-2">
              <FaLock className="text-red-400" size={12} />
              <h3 className="font-black text-xs text-white uppercase tracking-widest">Reset Student Password</h3>
            </div>
            <input
              type="password"
              placeholder="Enter new password (min 6 chars)"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              className="w-full px-4 py-2.5 bg-white/5 border border-white/10 rounded-lg text-white text-xs font-bold outline-none focus:border-orange-500/60 transition-all placeholder-white/20"
            />
            <button type="submit" disabled={resetting}
              className="w-full py-2.5 bg-red-500 hover:bg-red-600 text-white font-black text-xs uppercase tracking-widest rounded-lg transition-all active:scale-95 disabled:opacity-50">
              {resetting ? 'Resetting...' : 'Reset Password'}
            </button>
          </form>

          {/* Role Management */}
          <div className="bg-slate-100 rounded-xl p-5 space-y-4">
             <div className="flex items-center gap-2">
                <FaUserCircle className="text-slate-500" size={12} />
                <h3 className="font-black text-xs text-slate-700 uppercase tracking-widest">Account Role Management</h3>
             </div>
             <p className="text-[10px] text-slate-500 font-bold">Current Role: <span className="uppercase text-orange-600">{student.role}</span></p>
             
             <div className="flex gap-2">
                <button 
                  onClick={async () => {
                    if(!window.confirm("Are you sure you want to change this user's role?")) return;
                    setMsg(null);
                    try {
                      const res = await fetch(`${API_BASE_URL}/admin/manage_student.php`, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ action: 'update_role', id: student.id, role: student.role === 'admin' ? 'student' : 'admin' })
                      });
                      const data = await res.json();
                      setMsg({ type: data.status, text: data.message });
                      if (data.status === 'success') onSaved(); // This will refresh the list (and likely remove the user if they become admin)
                    } catch { setMsg({ type: 'error', text: 'Failed to update role.' }); }
                  }}
                  className={`flex-grow py-2.5 rounded-lg font-black text-xs uppercase tracking-widest transition-all active:scale-95 border ${
                    student.role === 'admin' 
                    ? 'bg-white text-slate-700 border-slate-200 hover:bg-slate-50' 
                    : 'bg-slate-800 text-white border-slate-800 hover:bg-slate-900'
                  }`}
                >
                  {student.role === 'admin' ? 'Demote to Student' : 'Promote to Admin'}
                </button>
             </div>
             <p className="text-[9px] text-slate-400 italic">* Note: Promoting a student to Admin will remove them from this Student List.</p>
          </div>

        </div>
      </div>
    </>
  );
};

// ─── Field Components ─────────────────────────────────────────────────────────
const Field = ({ label, name, value, onChange, type = 'text', icon }) => (
  <div>
    <label className="block text-[9px] font-black text-slate-400 uppercase tracking-widest mb-1">{label}</label>
    <div className="relative">
      {icon && <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-300 text-xs">{icon}</span>}
      <input type={type} name={name} value={value} onChange={onChange}
        className="w-full bg-slate-50 border border-slate-100 rounded-lg px-3 py-2 text-xs font-bold text-slate-700 outline-none focus:border-orange-400 transition-all pl-8" />
    </div>
  </div>
);

const SelectField = ({ label, name, value, onChange, options }) => (
  <div>
    <label className="block text-[9px] font-black text-slate-400 uppercase tracking-widest mb-1">{label}</label>
    <select name={name} value={value} onChange={onChange}
      className="w-full bg-slate-50 border border-slate-100 rounded-lg px-3 py-2 text-xs font-bold text-slate-700 outline-none focus:border-orange-400 transition-all">
      <option value="">— Select —</option>
      {options.map(o => <option key={o} value={o}>{o}</option>)}
    </select>
  </div>
);

// ─── Main StudentList ─────────────────────────────────────────────────────────
const StudentList = () => {
  const [students, setStudents]           = useState([]);
  const [loading, setLoading]             = useState(true);
  const [searchTerm, setSearchTerm]       = useState('');
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [drawerLoading, setDrawerLoading] = useState(false);
  const [showAddModal, setShowAddModal]   = useState(false);
  
  const [currentPage, setCurrentPage]     = useState(1);
  const [totalPages, setTotalPages]       = useState(1);
  const [totalStudents, setTotalStudents] = useState(0);
  const [roleFilter, setRoleFilter]       = useState('student'); // 'student' or 'admin'

  const fetchStudents = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/admin/get_students.php?page=${currentPage}&search=${searchTerm}&role=${roleFilter}`);
      const data = await response.json();
      if (data.status === 'success') {
        setStudents(data.data);
        setTotalPages(data.totalPages);
        setTotalStudents(data.total);
      }
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      setCurrentPage(1); // Reset to page 1 on new search
      fetchStudents();
    }, 500);
    return () => clearTimeout(delayDebounceFn);
  }, [searchTerm, roleFilter]);

  useEffect(() => {
    fetchStudents();
  }, [currentPage]);

  const openDrawer = async (studentId) => {
    setDrawerLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}/admin/manage_student.php?id=${studentId}`);
      const data = await res.json();
      if (data.status === 'success') setSelectedStudent(data.data);
    } catch (err) { console.error(err); }
    finally { setDrawerLoading(false); }
  };

  return (
    <>
      <div className="space-y-5">
        <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4">
          <div className="flex items-center gap-4">
            <div>
              <h2 className="text-lg font-black text-slate-800">{roleFilter === 'student' ? 'Registered Students' : 'System Administrators'}</h2>
              <p className="text-[10px] text-slate-400 font-bold">{totalStudents} {roleFilter === 'student' ? 'total students' : 'total admins'}</p>
            </div>
            
            {/* Role Switcher */}
            <div className="flex bg-slate-100 p-1 rounded-xl ml-4">
               <button 
                 onClick={() => setRoleFilter('student')}
                 className={`px-4 py-1.5 rounded-lg text-[10px] font-black uppercase tracking-widest transition-all ${roleFilter === 'student' ? 'bg-white text-orange-600 shadow-sm' : 'text-slate-400 hover:text-slate-600'}`}
               >
                 Students
               </button>
               <button 
                 onClick={() => setRoleFilter('admin')}
                 className={`px-4 py-1.5 rounded-lg text-[10px] font-black uppercase tracking-widest transition-all ${roleFilter === 'admin' ? 'bg-white text-orange-600 shadow-sm' : 'text-slate-400 hover:text-slate-600'}`}
               >
                 Admins
               </button>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className="relative w-full sm:w-64">
              <FaSearch className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 text-xs" />
              <input
                type="text"
                placeholder="Search by name, index, phone..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2.5 bg-white border border-slate-200 rounded-xl outline-none focus:border-orange-500 font-bold text-xs transition-all shadow-sm"
              />
            </div>
            <button
              onClick={() => setShowAddModal(true)}
              className="flex items-center gap-2 px-4 py-2.5 bg-orange-500 hover:bg-orange-600 text-white font-black text-xs uppercase tracking-widest rounded-xl shadow transition-all active:scale-95 whitespace-nowrap"
            >
              <FaPlus size={10} /> Add Student
            </button>
          </div>
        </div>

        <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-100">
                  <th className="px-5 py-3.5 text-[9px] font-black text-slate-400 uppercase tracking-widest">Student</th>
                  <th className="px-5 py-3.5 text-[9px] font-black text-slate-400 uppercase tracking-widest">Index No</th>
                  <th className="px-5 py-3.5 text-[9px] font-black text-slate-400 uppercase tracking-widest hidden sm:table-cell">Contact</th>
                  <th className="px-5 py-3.5 text-[9px] font-black text-slate-400 uppercase tracking-widest hidden md:table-cell">Academic</th>
                  <th className="px-5 py-3.5 text-[9px] font-black text-slate-400 uppercase tracking-widest text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-50">
                {loading ? (
                  <tr>
                    <td colSpan="5" className="px-6 py-10 text-center">
                      <div className="w-8 h-8 border-4 border-orange-500 border-t-transparent rounded-full animate-spin mx-auto"></div>
                    </td>
                  </tr>
                ) : students.length === 0 ? (
                  <tr>
                    <td colSpan="5" className="px-6 py-10 text-center text-slate-400 font-bold italic text-xs">No students found.</td>
                  </tr>
                ) : students.map((student) => (
                  <tr key={student.id} className="hover:bg-slate-50/60 transition-colors">
                    <td className="px-5 py-3.5">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-orange-100 flex items-center justify-center text-orange-500 font-black text-sm flex-shrink-0">
                          {student.full_name?.charAt(0)}
                        </div>
                        <div>
                          <p className="font-bold text-slate-800 text-xs">{student.full_name}</p>
                          <p className="text-[9px] text-slate-400">{student.email}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-5 py-3.5">
                      <span className="text-[9px] font-black text-orange-500 bg-orange-50 px-2 py-0.5 rounded tracking-widest">
                        {student.index_number}
                      </span>
                    </td>
                    <td className="px-5 py-3.5 text-xs font-bold text-slate-600 hidden sm:table-cell">{student.phone}</td>
                    <td className="px-5 py-3.5 hidden md:table-cell">
                      <p className="text-[10px] font-bold text-slate-700">{student.grade}</p>
                      <p className="text-[9px] text-slate-400">{student.medium} · {student.district}</p>
                    </td>
                    <td className="px-5 py-3.5 text-right">
                      <button
                        onClick={() => openDrawer(student.id)}
                        disabled={drawerLoading}
                        className="text-[10px] font-black text-white bg-orange-500 hover:bg-orange-600 px-3 py-1.5 rounded-lg transition-all active:scale-95 disabled:opacity-50"
                      >
                        {drawerLoading ? '...' : 'View Details'}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination Controls */}
          {totalPages > 1 && (
            <div className="px-5 py-4 border-t border-slate-50 flex items-center justify-between bg-slate-50/30">
              <p className="text-[10px] font-bold text-slate-400">
                Page <span className="text-slate-800">{currentPage}</span> of <span className="text-slate-800">{totalPages}</span>
              </p>
              <div className="flex items-center gap-2">
                <button
                  disabled={currentPage === 1}
                  onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                  className="px-3 py-1.5 bg-white border border-slate-200 rounded-lg text-[10px] font-black text-slate-600 hover:bg-slate-50 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Previous
                </button>
                <button
                  disabled={currentPage === totalPages}
                  onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                  className="px-3 py-1.5 bg-white border border-slate-200 rounded-lg text-[10px] font-black text-slate-600 hover:bg-slate-50 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Student Detail Drawer */}
      {selectedStudent && (
        <StudentDrawer
          student={selectedStudent}
          onClose={() => setSelectedStudent(null)}
          onSaved={() => { fetchStudents(); }}
        />
      )}

      {/* Add Student Modal */}
      {showAddModal && (
        <AddStudentModal
          onClose={() => setShowAddModal(false)}
          onAdded={() => { fetchStudents(); }}
        />
      )}
    </>
  );
};

export default StudentList;
